/**
 * API Configuration
 * Centralized configuration for API endpoints and related settings
 */

import { GOOGLE_MAPS_CONFIG } from './constants';

export interface ApiConfig {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  googleMapsApiKey: string;
}

// Environment-based configuration
const getEnvironmentConfig = (): ApiConfig => {
  // Check if we're in development, staging, or production
  const isDevelopment = import.meta.env.DEV;
  const apiUrl = import.meta.env.VITE_API_URL;
  const apiTimeout = import.meta.env.VITE_API_TIMEOUT;
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Default configuration
  const defaultConfig: ApiConfig = {
    // In development, use proxy path to avoid CORS issues
    baseUrl: 'https://www.durgapuja.network/api',
    timeout: 30000, // 30 seconds
    retryAttempts: 3,
    retryDelay: 1000, // 1 second
    googleMapsApiKey: googleMapsApiKey || GOOGLE_MAPS_CONFIG.DEFAULT_API_KEY, // Fallback API key
  };

  // Environment-specific overrides
  if (apiUrl) {
    // If VITE_API_URL is explicitly set, use it (overrides proxy)
    defaultConfig.baseUrl = apiUrl;
  } else if (!isDevelopment) {
    // Production fallback - should be set via environment variables
    console.warn('VITE_API_URL not set. Using default development URL.');
  }

  if (apiTimeout) {
    defaultConfig.timeout = parseInt(apiTimeout, 10);
  }

  return defaultConfig;
};

// Export the configuration
export const API_CONFIG = getEnvironmentConfig();

// API endpoint paths - centralized for easy maintenance
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    REQUEST_OTP: '/request-otp',
    VERIFY_OTP: '/verify-otp',
    RESEND_OTP: '/resend-otp',
    FORGOT_PASSWORD: '/forgot-password',
    VERIFY_RESET_OTP: '/verify-reset-otp',
    RESET_PASSWORD: '/reset-password',
  },

  // Admin User Management
  ADMIN_USERS: {
    BASE: '/admin-users',
    BY_ID: (id: number) => `/admin-users/${id}`,
  },

  // Event Management
  EVENTS: {
    PUBLIC: '/events/public',
    UPCOMING: '/events/upcoming',
    BY_ID: (id: number) => `/events/${id}`,
    BASE: '/events',
    MY_EVENTS: '/events/my-events',
    PENDING: '/events/pending',
    VERIFY: (id: number) => `/events/${id}/verify`,
    BULK_VERIFY: '/events/bulk-verify',
    BULK_CREATE: '/events/bulk/create',
    STATS: '/events/stats/admin',
    STATS_DASHBOARD: '/events/stats/dashboard',
    ADMIN: '/events/admin',
    IMAGES: (id: number) => `/events/${id}/images`,
    UPLOAD_IMAGE: (id: number) => `/events/${id}/upload-image`,
    THUMBNAIL: (id: number) => `/events/${id}/thumbnail`,
    ALL: '/events/all',
    // New API structure endpoints
    SEARCH: '/events/search',
    FEATURED: '/events/featured',
    TRENDING: '/events/trending',
    BY_LOCATION: '/events/by-location',
    BY_DATE_RANGE: '/events/by-date-range',
    CREATE_WITH_USER: '/events/create-with-user',
  },

  // User Management
  USERS: {
    PROFILE: (id: number) => `/user/${id}`,
    ADMIN_USERS: '/admin/users', // Legacy endpoint
  },


} as const;

// Helper function to build full URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.baseUrl}${endpoint}`;
};

// Helper function to get API configuration
export const getApiConfig = (): ApiConfig => {
  return API_CONFIG;
};

// Helper function to update configuration at runtime (if needed)
export const updateApiConfig = (newConfig: Partial<ApiConfig>): void => {
  Object.assign(API_CONFIG, newConfig);
};

// Export common headers
export const getDefaultHeaders = (): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
  };
};

export const getAuthHeaders = (token: string): Record<string, string> => {
  return {
    ...getDefaultHeaders(),
    'Authorization': `Bearer ${token}`,
  };
};

// Google Maps API Key helper
export const getGoogleMapsApiKey = (): string => {
  return API_CONFIG.googleMapsApiKey;
};

// Development helper - auto-run verification if debug is enabled
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEBUG === 'true') {
  console.log('🔧 API Configuration loaded:', {
    baseUrl: API_CONFIG.baseUrl,
    timeout: API_CONFIG.timeout,
    googleMapsApiKey: API_CONFIG.googleMapsApiKey ? 'Present' : 'Missing',
    environment: import.meta.env.VITE_APP_ENV || 'development',
  });
}